// frontend/src/pages/Register.jsx
import React, { useEffect, useRef, useState } from "react";
import apiClient from "../api/apiClient";
import { useToast } from "../contexts/ToastContext";
import LoadingSpinner from "../components/UI/LoadingSpinner";
import { useNavigate } from "react-router-dom";

const toPositiveIntOrEmpty = (v) => {
  const n = Number(v);
  return Number.isInteger(n) && n > 0 ? String(n) : "";
};

const buildDepartmentDisplay = (dep) => {
  if (!dep) return "";
  const code = String(dep.code ?? "").trim();
  const name = String(dep.name ?? "").trim();
  if (code && name) return `${code} - ${name}`;
  return code || name || "";
};

const buildAdministrativeUnitDisplay = (unit) => {
  if (!unit) return "";
  const code = String(unit.code ?? "").trim();
  const name = String(unit.name ?? "").trim();
  if (code && name) return `${code} ${name}`;
  return code || name || "";
};

const REGISTRATION_EMAIL_DOMAIN = "@uach.mx";
const INVALID_REGISTRATION_EMAIL_MSG =
  "Ese correo no es valido para registro. Usa tu correo institucional de la UACH (@uach.mx).";

const isUachEmail = (value) =>
  String(value || "")
    .trim()
    .toLowerCase()
    .endsWith(REGISTRATION_EMAIL_DOMAIN);

const logUnexpectedError = (err) => {
  const status = Number(err?.response?.status || 0);
  if (!status || status >= 500) {
    console.error(err);
  }
};

const getErrorMessage = (err, fallbackMessage) => {
  const data = err?.response?.data;

  if (typeof data === "string" && data.trim()) {
    return data;
  }

  if (data?.error && typeof data.error === "string") {
    return data.error;
  }

  if (data?.detail && typeof data.detail === "string") {
    return data.detail;
  }

  if (data?.message && typeof data.message === "string") {
    return data.message;
  }

  if (Array.isArray(data?.non_field_errors) && data.non_field_errors.length > 0) {
    return data.non_field_errors.join(" ");
  }

  if (Array.isArray(data?.email) && data.email.length > 0) {
    return data.email.join(" ");
  }

  return fallbackMessage;
};

export default function Register() {
  const { showToast } = useToast();
  const navigate = useNavigate();

  const showToastRef = useRef(showToast);
  const catalogsRequestedRef = useRef(false);
  const catalogErrorToastShownRef = useRef(false);

  useEffect(() => {
    showToastRef.current = showToast;
  }, [showToast]);

  const [step, setStep] = useState(1);
  const [loading, setLoading] = useState(false);
  const [catalogsLoading, setCatalogsLoading] = useState(false);
  const [departments, setDepartments] = useState([]);
  const [administrativeUnits, setAdministrativeUnits] = useState([]);
  const [catalogsError, setCatalogsError] = useState("");
  const [sendCodeError, setSendCodeError] = useState("");

  const [formData, setFormData] = useState({
    employee_number: "",
    first_name: "",
    last_name: "",
    department: "", // legacy/display temporal
    department_ref_id: "", // FK canónico de departamento
    administrative_unit_id: "", // FK canónico de unidad administrativa
    extension_number: "",
    email: "",
    password: "",
    confirm_password: "",
    code: "",
    profile_picture: null,
  });

  useEffect(() => {
    if (step !== 2) return;
    if (catalogsRequestedRef.current) return;

    let cancelled = false;
    catalogsRequestedRef.current = true;
    setCatalogsLoading(true);
    setCatalogsError("");

    const loadCatalogs = async () => {
      const [departmentsResult, administrativeUnitsResult] = await Promise.allSettled([
        apiClient.get("/catalogs/departments/"),
        apiClient.get("/users/administrative-units/"),
      ]);

      if (cancelled) return;

      let nextDepartments = [];
      let nextAdministrativeUnits = [];
      const failedParts = [];

      if (departmentsResult.status === "fulfilled") {
        nextDepartments = Array.isArray(departmentsResult.value?.data)
          ? departmentsResult.value.data
          : [];
      } else {
        console.error("Error cargando departamentos:", departmentsResult.reason);
        failedParts.push("departamentos");
      }

      if (administrativeUnitsResult.status === "fulfilled") {
        nextAdministrativeUnits = Array.isArray(administrativeUnitsResult.value?.data)
          ? administrativeUnitsResult.value.data
          : [];
      } else {
        console.error(
          "Error cargando unidades administrativas:",
          administrativeUnitsResult.reason
        );
        failedParts.push("unidades administrativas");
      }

      setDepartments(nextDepartments);
      setAdministrativeUnits(nextAdministrativeUnits);

      if (failedParts.length > 0) {
        const errorMessage = `No se pudieron cargar ${failedParts.join(" y ")}.`;
        setCatalogsError(errorMessage);

        if (!catalogErrorToastShownRef.current) {
          showToastRef.current(
            `${errorMessage} Revisa permisos del endpoint o recarga la página cuando quede disponible.`,
            "error"
          );
          catalogErrorToastShownRef.current = true;
        }
      }
    };

    loadCatalogs()
      .catch((err) => {
        if (cancelled) return;
        console.error("Error inesperado cargando catálogos:", err);

        const message = "Error inesperado cargando catálogos del registro.";
        setCatalogsError(message);

        if (!catalogErrorToastShownRef.current) {
          showToastRef.current(message, "error");
          catalogErrorToastShownRef.current = true;
        }
      })
      .finally(() => {
        if (!cancelled) {
          setCatalogsLoading(false);
        }
      });

    return () => {
      cancelled = true;
    };
  }, [step]);

  const setRequiredMessage = (e, message) => {
    e.target.setCustomValidity(message);
    e.target.oninput = () => e.target.setCustomValidity("");
  };

  const handleChange = (e) => {
    const { name, value, files } = e.target;

    if (name === "email") {
      setSendCodeError("");
    }

    setFormData((prev) => ({
      ...prev,
      [name]: files ? files[0] : value,
    }));
  };

  const handleDepartmentChange = (e) => {
    const selectedId = toPositiveIntOrEmpty(e.target.value);

    if (!selectedId) {
      setFormData((prev) => ({
        ...prev,
        department_ref_id: "",
        department: "",
      }));
      return;
    }

    const dep = departments.find((d) => String(d.id) === String(selectedId));
    const display = buildDepartmentDisplay(dep);

    setFormData((prev) => ({
      ...prev,
      department_ref_id: selectedId,
      department: display,
    }));
  };

  const handleAdministrativeUnitChange = (e) => {
    const selectedId = toPositiveIntOrEmpty(e.target.value);

    setFormData((prev) => ({
      ...prev,
      administrative_unit_id: selectedId,
    }));
  };

  const handleSendCode = async (e) => {
    e.preventDefault();
    if (loading) return;

    const normalizedEmail = String(formData.email || "").trim().toLowerCase();

    if (!isUachEmail(normalizedEmail)) {
      setSendCodeError(INVALID_REGISTRATION_EMAIL_MSG);
      showToast(INVALID_REGISTRATION_EMAIL_MSG, "error");
      return;
    }

    setSendCodeError("");
    setLoading(true);
    try {
      await apiClient.post("/users/send-code/", {
        email: normalizedEmail,
      });

      showToast("Código de registro enviado a tu Email", "success");
      setStep(2);
    } catch (err) {
      logUnexpectedError(err);
      const actualMessage = getErrorMessage(err, "No se pudo enviar el codigo de registro. Verifica que tu correo sea institucional @uach.mx.");
      setSendCodeError(actualMessage);
      showToast(actualMessage, "error");
    } finally {
      setLoading(false);
    }
  };

  const handleRegister = async (e) => {
    e.preventDefault();
    if (loading) return;

    if (formData.password !== formData.confirm_password) {
      showToast("Las contraseñas no coinciden", "error");
      return;
    }

    if (!/^\d{6}$/.test(String(formData.code || "").trim())) {
      showToast("El código debe ser numérico de 6 dígitos.", "error");
      return;
    }

    if (!toPositiveIntOrEmpty(formData.administrative_unit_id)) {
      showToast("Por favor seleccione una unidad administrativa válida.", "error");
      return;
    }

    if (!toPositiveIntOrEmpty(formData.department_ref_id)) {
      showToast("Por favor seleccione un departamento válido.", "error");
      return;
    }

    setLoading(true);
    try {
      const payload = new FormData();

      payload.append("employee_number", String(formData.employee_number || "").trim());
      payload.append("first_name", String(formData.first_name || "").trim());
      payload.append("last_name", String(formData.last_name || "").trim());
      payload.append("extension_number", String(formData.extension_number || "").trim());
      payload.append("email", String(formData.email || "").trim());
      payload.append("password", formData.password || "");
      payload.append("confirm_password", formData.confirm_password || "");
      payload.append("code", String(formData.code || "").trim());

      payload.append(
        "administrative_unit_id",
        String(formData.administrative_unit_id || "").trim()
      );

      payload.append(
        "department_ref_id",
        String(formData.department_ref_id || "").trim()
      );

      if (String(formData.department || "").trim()) {
        payload.append("department", String(formData.department).trim());
      }

      if (formData.profile_picture) {
        payload.append("profile_picture", formData.profile_picture);
      }

      await apiClient.post("/users/register/", payload);

      showToast("¡Registro exitoso! Ahora puedes loguearte", "success");
      navigate("/login");
    } catch (err) {
      logUnexpectedError(err);
      showToast(
        getErrorMessage(err, "Error al registrar, por favor revisa tus datos."),
        "error"
      );
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-tr from-indigo-50 via-white to-pink-50 flex flex-col justify-center items-center px-6">
      <div className="text-center mb-12 max-w-lg">
        <img
          src={`${process.env.PUBLIC_URL}/images/uach_logo.png`}
          alt="UACH Logo"
          className="mx-auto mb-6 w-32 h-auto"
        />
        <h1 className="text-5xl font-extrabold text-indigo-700 drop-shadow-md mb-3">
          Sistema Integral de Adquisiciones
        </h1>
        <p className="text-xl text-indigo-600 leading-relaxed tracking-wide">
          Facultad de Ingeniería
          <br />
          Universidad Autónoma de Chihuahua
        </p>
      </div>

      <div className="bg-white shadow-2xl rounded-3xl p-10 max-w-md w-full ring-1 ring-indigo-100">
        {step === 1 && (
          <>
            <h2 className="text-2xl font-semibold mb-4 text-gray-700 tracking-wide">
              Solicitar código de registro
            </h2>

            <form onSubmit={handleSendCode} className="space-y-4">
              <div>
                <label className="block mb-2 font-semibold text-gray-700 tracking-wide">
                  Email Universitario
                </label>
                <input
                  type="email"
                  name="email"
                  value={formData.email}
                  onChange={handleChange}
                  required
                  onInvalid={(e) => {
                    if (!e.target.value) {
                      e.target.setCustomValidity(
                        "Por favor ingrese su correo universitario"
                      );
                    } else {
                      e.target.setCustomValidity(
                        "Por favor ingrese un correo válido (ejemplo@uach.mx)"
                      );
                    }
                  }}
                  onInput={(e) => e.target.setCustomValidity("")}
                  className="w-full border rounded p-2"
                  placeholder="Correo universitario"
                />
                {!!sendCodeError && (
                  <p className="mt-2 text-sm text-red-600">{sendCodeError}</p>
                )}
              </div>

              <button
                type="submit"
                disabled={loading}
                className="w-full bg-gradient-to-r from-indigo-600 to-pink-500 hover:from-indigo-700 hover:to-pink-600 text-white font-bold py-3 rounded-3xl shadow-lg transition-transform active:scale-95 disabled:opacity-70"
              >
                {loading ? <LoadingSpinner /> : "Enviar Código"}
              </button>
            </form>
          </>
        )}

        {step === 2 && (
          <>
            <h2 className="text-2xl font-extrabold mb-6 text-gray-700 tracking-wide">
              Completa el registro
            </h2>

            {catalogsLoading && (
              <div className="mb-4 rounded-xl border border-indigo-100 bg-indigo-50 px-4 py-3 text-sm text-indigo-700">
                Cargando departamentos y unidades administrativas...
              </div>
            )}

            {!!catalogsError && (
              <div className="mb-4 rounded-xl border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-700">
                {catalogsError}
              </div>
            )}

            <form onSubmit={handleRegister} className="space-y-4">
              <div>
                <label className="block mb-2 font-semibold text-gray-700 tracking-wide">
                  Código de registro
                </label>
                <input
                  type="text"
                  name="code"
                  value={formData.code}
                  onChange={(e) => {
                    const onlyDigits = e.target.value.replace(/\D/g, "").slice(0, 6);
                    setFormData((p) => ({ ...p, code: onlyDigits }));
                  }}
                  inputMode="numeric"
                  pattern="\d{6}"
                  maxLength={6}
                  placeholder="000000"
                  required
                  onInvalid={(e) =>
                    setRequiredMessage(e, "Por favor ingrese el código de registro")
                  }
                  className="w-full border rounded p-2"
                />
              </div>

              <div>
                <label className="block mb-2 font-semibold text-gray-700 tracking-wide">
                  Número de empleado
                </label>
                <input
                  type="text"
                  name="employee_number"
                  value={formData.employee_number}
                  onChange={handleChange}
                  required
                  onInvalid={(e) =>
                    setRequiredMessage(e, "Por favor ingrese su número de empleado")
                  }
                  className="w-full border rounded p-2"
                />
              </div>

              <div>
                <label className="block mb-2 font-semibold text-gray-700 tracking-wide">
                  Nombre
                </label>
                <input
                  type="text"
                  name="first_name"
                  value={formData.first_name}
                  onChange={handleChange}
                  required
                  onInvalid={(e) => setRequiredMessage(e, "Por favor ingrese su nombre")}
                  className="w-full border rounded p-2"
                />
              </div>

              <div>
                <label className="block mb-2 font-semibold text-gray-700 tracking-wide">
                  Apellidos
                </label>
                <input
                  type="text"
                  name="last_name"
                  value={formData.last_name}
                  onChange={handleChange}
                  required
                  onInvalid={(e) =>
                    setRequiredMessage(e, "Por favor ingrese sus apellidos")
                  }
                  className="w-full border rounded p-2"
                />
              </div>

              <div>
                <label className="block mb-2 font-semibold text-gray-700 tracking-wide">
                  Unidad Administrativa
                </label>

                <select
                  name="administrative_unit_id"
                  value={formData.administrative_unit_id}
                  onChange={handleAdministrativeUnitChange}
                  required
                  disabled={catalogsLoading || administrativeUnits.length === 0}
                  onInvalid={(e) =>
                    setRequiredMessage(e, "Por favor seleccione una unidad administrativa")
                  }
                  className="w-full border rounded p-2 disabled:bg-gray-100"
                >
                  <option value="">Seleccionar Unidad Administrativa</option>
                  {administrativeUnits.map((unit) => (
                    <option key={unit.id} value={unit.id}>
                      {buildAdministrativeUnitDisplay(unit)}
                    </option>
                  ))}
                </select>
              </div>

              <div>
                <label className="block mb-2 font-semibold text-gray-700 tracking-wide">
                  Departamento
                </label>

                <select
                  name="department_ref_id"
                  value={formData.department_ref_id}
                  onChange={handleDepartmentChange}
                  required
                  disabled={catalogsLoading || departments.length === 0}
                  onInvalid={(e) =>
                    setRequiredMessage(e, "Por favor seleccione un departamento")
                  }
                  className="w-full border rounded p-2 disabled:bg-gray-100"
                >
                  <option value="">Seleccionar Departamento</option>
                  {departments.map((dep) => (
                    <option key={dep.id} value={dep.id}>
                      {dep.code} - {dep.name}
                    </option>
                  ))}
                </select>
              </div>

              <div>
                <label className="block mb-2 font-semibold text-gray-700 tracking-wide">
                  Número de extensión
                </label>
                <input
                  type="text"
                  name="extension_number"
                  value={formData.extension_number}
                  onChange={handleChange}
                  required
                  onInvalid={(e) =>
                    setRequiredMessage(e, "Por favor ingrese su número de extensión")
                  }
                  className="w-full border rounded p-2"
                />
              </div>

              <div>
                <label className="block mb-2 font-semibold text-gray-700 tracking-wide">
                  Contraseña
                </label>
                <input
                  type="password"
                  name="password"
                  value={formData.password}
                  onChange={handleChange}
                  required
                  onInvalid={(e) =>
                    setRequiredMessage(e, "Por favor ingrese su contraseña")
                  }
                  className="w-full border rounded p-2"
                />
              </div>

              <div>
                <label className="block mb-2 font-semibold text-gray-700 tracking-wide">
                  Confirmar Contraseña
                </label>
                <input
                  type="password"
                  name="confirm_password"
                  value={formData.confirm_password}
                  onChange={handleChange}
                  required
                  onInvalid={(e) =>
                    setRequiredMessage(e, "Por favor confirme su contraseña")
                  }
                  className="w-full border rounded p-2"
                />
              </div>

              <div>
                <label className="block mb-2 font-semibold text-gray-700 tracking-wide">
                  Imagen de perfil (opcional)
                </label>
                <input
                  type="file"
                  name="profile_picture"
                  accept="image/*"
                  onChange={handleChange}
                  className="w-full border rounded p-2"
                />
              </div>

              <button
                type="submit"
                disabled={
                  loading ||
                  catalogsLoading ||
                  departments.length === 0 ||
                  administrativeUnits.length === 0
                }
                className="w-full bg-gradient-to-r from-indigo-600 to-pink-500 hover:from-indigo-700 hover:to-pink-600 text-white font-bold py-3 rounded-3xl shadow-lg transition-transform active:scale-95 disabled:opacity-70"
              >
                {loading ? <LoadingSpinner /> : "Registrar"}
              </button>
            </form>
          </>
        )}
      </div>
    </div>
  );
}
